import { useEffect, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../config/firebase";
import Route from "../../models/Route";

/**
 * Hook to live fetch a single route by ID.
 */
export function useRouteListen(routeId, { enabled = false }) {
  const [data, setData] = useState(undefined);
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const requestIdRef = useRef(0); // Request 2 overrides request 1 if request 1 is not yet completed.

  useEffect(() => {
    if (!routeId || !enabled) {
      requestIdRef.current += 1;
      setData(null);
      setIsPending(false);
      setIsSuccess(false);
      setError(null);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setIsPending(true);
    setIsSuccess(false);
    setError(null);

    const ref = doc(db, "routes", String(routeId));
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        if (snapshot.exists()) {
          setData(new Route({ id: routeId, ...snapshot.data() }));
        } else {
          setData(null);
        }

        setIsSuccess(true);
        setIsPending(false);
      },
      (err) => {
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        console.error("Failed to listen to route by ID.", err);
        setError(err);
        setData(null);
        setIsSuccess(false);
        setIsPending(false);
      },
    );

    return () => {
      requestIdRef.current += 1;
      unsubscribe();
    };
  }, [routeId, enabled]);

  return { data, isSuccess, isPending, error };
}
